import type { MessageList, RouteResponse } from "@mailhand/core/api";
import { MESSAGE_STATES, type MessageId, type MessageRow, type MessageState } from "@mailhand/core/schemas";
import { useEffect, useMemo, useRef, useState } from "react";
import { FOLLOW_UP_LABEL, MESSAGE_STATE_LABEL } from "../../app/labels.js";
import { Link } from "../../app/router.js";
import { useApi } from "../../app/services.js";
import { isSetupComplete, useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { isSameDay, shortTime } from "../../lib/format.js";
import { useResource } from "../../lib/useResource.js";
import { Badge, Button, EmptyState, ErrorNote, PageHead, Select, Spinner, useToast } from "../../ui/index.js";
import { BulkOverrideDrawer } from "../shared/BulkOverrideDrawer.js";
import { bulkSummary } from "../shared/bulkOverride.js";
import { canDismiss, dismissMessages } from "../shared/dismiss.js";

const PAGE_SIZE = 100;
const DAY_MS = 24 * 60 * 60 * 1000;

const RANGES = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "Everything loaded" },
] as const;
type RangeValue = (typeof RANGES)[number]["value"];

const FOLLOW_UP_FILTERS = [
  { value: "all", label: "Any follow-up" },
  { value: "open", label: "Open follow-ups" },
  { value: "closed", label: "Closed follow-ups" },
] as const;
type FollowUpFilter = (typeof FOLLOW_UP_FILTERS)[number]["value"];

interface Filters {
  state: MessageState | "all";
  action: string;
  followUp: FollowUpFilter;
  range: RangeValue;
}

function rangeStart(range: RangeValue, now: Date): number {
  if (range === "today") return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (range === "7d") return now.getTime() - 7 * DAY_MS;
  if (range === "30d") return now.getTime() - 30 * DAY_MS;
  return 0;
}

function applyFilters(rows: readonly MessageRow[], filters: Filters, now: Date): MessageRow[] {
  const since = rangeStart(filters.range, now);
  return rows.filter(
    (row) =>
      (filters.state === "all" || row.state === filters.state) &&
      (filters.action === "all" || row.actionName === filters.action) &&
      (filters.followUp === "all" || row.followUp === filters.followUp) &&
      new Date(row.receivedAt).getTime() >= since,
  );
}

/** A message still being handled cannot be overridden. */
function isSelectable(row: MessageRow): boolean {
  return row.state !== "received" && row.state !== "running";
}

function isMessageState(value: string): value is MessageState {
  return MESSAGE_STATES.some((state) => state === value);
}

function isRange(value: string): value is RangeValue {
  return RANGES.some((range) => range.value === value);
}

function isFollowUpFilter(value: string): value is FollowUpFilter {
  return FOLLOW_UP_FILTERS.some((option) => option.value === value);
}

export function ActivityScreen() {
  const api = useApi();
  const toast = useToast();
  const { view, actions, reloadApprovals } = useTenant();
  const more = useTask();
  const bulk = useTask();
  const [selected, setSelected] = useState<ReadonlySet<MessageId>>(new Set());
  const [bulkOverrideOpen, setBulkOverrideOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({ state: "all", action: "all", followUp: "all", range: "7d" });
  const { resource, replace, reload } = useResource<MessageList>(() => api.call("messagesList", { query: { limit: PAGE_SIZE } }), [api]);
  // The loaded pages hold only recent messages; this list holds every open follow-up.
  const followUps = useResource<RouteResponse<"followUpsList">>(() => api.call("followUpsList", {}), [api]);
  const now = new Date();

  const rows = resource.status === "ready" ? resource.data.messages : [];
  const openFollowUps = followUps.resource.status === "ready" ? followUps.resource.data.messages : [];
  const showsOpenFollowUps = filters.followUp === "open";
  const listed = showsOpenFollowUps ? openFollowUps : rows;
  const listStatus = showsOpenFollowUps ? followUps.resource : resource;
  const actionNames = useMemo(() => [...new Set(listed.flatMap((row) => (row.actionName === undefined ? [] : [row.actionName])))].sort(), [listed]);
  const visible = applyFilters(listed, filters, now);
  const selectableRows = visible.filter(isSelectable);
  // Only rows that the filters show now take part, so a filter change cannot act on hidden rows.
  const selectedRows = selectableRows.filter((row) => selected.has(row.id));
  const dismissable = selectedRows.filter((row) => canDismiss(row.state));

  function toggle(id: MessageId, on: boolean): void {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
  }

  function toggleAll(on: boolean): void {
    setSelected(on ? new Set([...selected, ...selectableRows.map((row) => row.id)]) : new Set());
  }

  async function afterBulk(): Promise<void> {
    setSelected(new Set());
    await Promise.all([reload(), followUps.reload(), reloadApprovals()]);
  }

  async function dismissSelected(): Promise<void> {
    let summary = "";
    const ok = await bulk.run(async () => {
      summary = bulkSummary("Dismissed", await dismissMessages(api, actions, dismissable.map((row) => row.id)));
      await afterBulk();
    });
    if (ok) toast.show(summary, "success");
  }

  async function loadOlder(cursor: string, current: MessageList): Promise<void> {
    await more.run(async () => {
      const page = await api.call("messagesList", { query: { limit: PAGE_SIZE, cursor } });
      replace({ messages: [...current.messages, ...page.messages], nextCursor: page.nextCursor });
    });
  }

  const counts = {
    waiting: rows.filter((row) => row.state === "waiting").length,
    held: rows.filter((row) => row.state === "held").length,
    doneToday: rows.filter((row) => row.state === "done" && isSameDay(new Date(row.receivedAt), now)).length,
  };

  return (
    <>
      <PageHead title="Activity" />
      {isSetupComplete(view, actions) ? null : (
        <Link to="/setup" className="banner">
          <span className="live-dot" aria-hidden="true" />
          <span>Finish setup so email starts flowing.</span>
          <span className="banner-cta">Continue setup →</span>
        </Link>
      )}
      <div className="stats">
        <StatTile label="Waiting for you" value={counts.waiting} tone="amber" onClick={() => { setFilters({ ...filters, state: "waiting", range: "all" }); }} />
        <StatTile label="Held" value={counts.held} tone="orange" onClick={() => { setFilters({ ...filters, state: "held", range: "all" }); }} />
        <StatTile label="Done today" value={counts.doneToday} tone="green" onClick={() => { setFilters({ ...filters, state: "done", range: "today" }); }} />
        <StatTile label="Open follow-ups" value={openFollowUps.length} tone="blue" onClick={() => { setFilters({ ...filters, state: "all", followUp: "open", range: "all" }); }} />
      </div>

      <div className="filters">
        <Select aria-label="State" value={filters.state} onChange={(event) => {
            const value = event.target.value;
            setFilters({ ...filters, state: isMessageState(value) ? value : "all" });
          }}>
          <option value="all">Any state</option>
          {MESSAGE_STATES.map((state) => (
            <option key={state} value={state}>
              {MESSAGE_STATE_LABEL[state].label}
            </option>
          ))}
        </Select>
        <Select aria-label="Action" value={filters.action} onChange={(event) => { setFilters({ ...filters, action: event.target.value }); }}>
          <option value="all">Any action</option>
          {actionNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
        <Select aria-label="Follow-up" value={filters.followUp} onChange={(event) => {
            const value = event.target.value;
            if (isFollowUpFilter(value)) setFilters({ ...filters, followUp: value });
          }}>
          {FOLLOW_UP_FILTERS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Select aria-label="Date range" value={filters.range} onChange={(event) => {
            const value = event.target.value;
            if (isRange(value)) setFilters({ ...filters, range: value });
          }}>
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </Select>
      </div>

      {listStatus.status === "loading" ? (
        <div className="list-loading">
          <Spinner />
        </div>
      ) : null}
      {listStatus.status === "error" ? <ErrorNote message={listStatus.message} onRetry={() => void (showsOpenFollowUps ? followUps.reload() : reload())} /> : null}
      {listStatus.status === "ready" && resource.status === "ready" ? (
        <>
          {visible.length === 0 ? (
            <EmptyState title={rows.length === 0 ? "No email yet." : "Nothing matches these filters."}>
              {rows.length === 0 ? "Forward a test email to your address and it shows up here." : null}
            </EmptyState>
          ) : (
            <>
              <SelectAll selectable={selectableRows.length} selected={selectedRows.length} onChange={toggleAll} />
              <ul className="message-list">
                {visible.map((row) => (
                  <MessageListItem key={row.id} row={row} now={now} selected={selected.has(row.id)} onSelect={(on) => { toggle(row.id, on); }} />
                ))}
              </ul>
            </>
          )}
          {showsOpenFollowUps ? null : <LoadOlder list={resource.data} busy={more.busy} onLoad={(cursor, list) => void loadOlder(cursor, list)} />}
        </>
      ) : null}
      {selectedRows.length === 0 ? null : (
        <div className="bulk-bar" role="region" aria-label="Selected messages">
          <span className="bulk-count">{selectedRows.length} selected</span>
          <div className="button-row">
            <Button onClick={() => void dismissSelected()} busy={bulk.busy} disabled={dismissable.length === 0} title={dismissable.length === 0 ? "Only held or waiting messages can be dismissed." : undefined}>
              Dismiss {dismissable.length}
            </Button>
            <Button variant="primary" onClick={() => { setBulkOverrideOpen(true); }} disabled={bulk.busy}>
              Change action
            </Button>
            <Button variant="ghost" onClick={() => { setSelected(new Set()); }} disabled={bulk.busy}>
              Clear
            </Button>
          </div>
        </div>
      )}
      <BulkOverrideDrawer
        open={bulkOverrideOpen}
        onClose={() => { setBulkOverrideOpen(false); }}
        rows={selectedRows}
        onDone={() => {
          setBulkOverrideOpen(false);
          void afterBulk();
        }}
      />
    </>
  );
}

function LoadOlder({ list, busy, onLoad }: { list: MessageList; busy: boolean; onLoad: (cursor: string, list: MessageList) => void }) {
  const cursor = list.nextCursor;
  if (cursor === null) return null;
  return (
    <div className="list-more">
      <Button busy={busy} onClick={() => { onLoad(cursor, list); }}>
        Load older email
      </Button>
    </div>
  );
}

function StatTile({ label, value, tone, onClick }: { label: string; value: number; tone: "amber" | "orange" | "green" | "blue"; onClick: () => void }) {
  return (
    <button type="button" className={`stat stat-${tone}`} onClick={onClick}>
      <span className="stat-label">
        <span className="stat-dot" aria-hidden="true" />
        {label}
      </span>
      <span className="stat-value mono">{value}</span>
    </button>
  );
}

function SelectAll({ selectable, selected, onChange }: { selectable: number; selected: number; onChange: (on: boolean) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const all = selectable > 0 && selected === selectable;
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = selected > 0 && !all;
  }, [selected, all]);
  return (
    <label className="checkbox list-select-all">
      <input ref={ref} type="checkbox" checked={all} disabled={selectable === 0} onChange={(event) => { onChange(event.target.checked); }} />
      <span>{selected === 0 ? "Select all" : `${selected} of ${selectable} selected`}</span>
    </label>
  );
}

function MessageListItem({ row, now, selected, onSelect }: { row: MessageRow; now: Date; selected: boolean; onSelect: (on: boolean) => void }) {
  const state = MESSAGE_STATE_LABEL[row.state];
  const from = row.fromName ?? row.from;
  return (
    <li className={selected ? "message-item is-selected" : "message-item"}>
      <input
        type="checkbox"
        className="message-select"
        aria-label={`Select the message from ${from}`}
        checked={selected}
        disabled={!isSelectable(row)}
        title={isSelectable(row) ? undefined : "This message is still being handled."}
        onChange={(event) => { onSelect(event.target.checked); }}
      />
      <Link to={`/messages/${row.id}`} className="message-row">
        <span className="message-time mono">{shortTime(row.receivedAt, now)}</span>
        <span className="message-main">
          <span className="message-from">{from}</span>
          <span className="message-subject">{row.subject === "" ? "(no subject)" : row.subject}</span>
          <span className="message-preview">{row.textPreview}</span>
        </span>
        <span className="message-meta">
          {row.actionName === undefined ? null : <span className="message-action">{row.actionName}</span>}
          {row.followUp === "none" ? null : <Badge tone={FOLLOW_UP_LABEL[row.followUp].tone}>{FOLLOW_UP_LABEL[row.followUp].label}</Badge>}
          <Badge tone={state.tone} dot>
            {state.label}
          </Badge>
        </span>
      </Link>
    </li>
  );
}
